import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators'
import { AxiosError } from 'axios'
import { codigoDoErro, getRequestErrorMessage, semPrefixoDeCodigo } from '@/helpers/axios-helpers'
import {
	ErroNoHistorico,
	lerHistoricoDeErros,
	limparHistoricoDeErros,
	registrarErroNoHistorico,
} from '@/shareds/historico-de-erros'
import store from '@/store/vuex/index'

@Module({
	name: 'alert',
	store,
	dynamic: true,
	namespaced: true,
})
class AlertModule extends VuexModule {
	message: Message = {
		text: '',
		type: 'success',
	}
	show = false

	/**
	 * Erros do dia que este navegador já mostrou, para o usuário reabrir o que fechou sem ler.
	 *
	 * Fica no estado do Vuex (e não só no localStorage) porque o badge do menu "?" precisa reagir na
	 * hora em que o erro acontece. A persistência é feita pelo shared, a cada registro.
	 */
	historico: ErroNoHistorico[] = lerHistoricoDeErros()

	@Mutation
	setShow(show: boolean) {
		this.show = show
	}

	@Mutation
	setMessage(message: Message) {
		this.message = message
		this.show = true
	}

	get getErrorMsg() {
		return this.message.text
	}

	@Mutation
	setError(error: SetMessageParams | unknown) {
		this.message = createMessageFromParams(null, 'error', error as SetMessageParams)
		this.show = true
		this.historico = registrarNoHistorico(this.message)
	}

	@Mutation
	setErrorComPrefixo(error: ErroComPrefixo) {
		this.message = createMessageFromParams(error.prefixo, 'error', error.error as SetMessageParams)
		this.show = true
		this.historico = registrarNoHistorico(this.message)
	}

	/**
	 * Relê o histórico do localStorage ao abrir o diálogo.
	 *
	 * A releitura resolve dois casos que o estado em memória não cobre: a aba aberta desde ontem (o
	 * filtro do dia só é aplicado na leitura) e o erro que aconteceu em OUTRA aba, já que o
	 * localStorage é compartilhado entre as abas do mesmo navegador.
	 */
	@Mutation
	abrirHistorico() {
		this.historico = lerHistoricoDeErros()
	}

	@Mutation
	limparHistorico() {
		limparHistoricoDeErros()
		this.historico = []
	}

	/** Total do dia, mostrado no item do menu. O histórico já vem filtrado pelo dia corrente. */
	get quantidadeDeErrosDeHoje() {
		return this.historico.length
	}

	@Mutation
	setSuccess(text: SetMessageParams) {
		this.message = createMessageFromParams(null, 'success', text)
		this.show = true
	}

	@Mutation
	setInfo(text: SetMessageParams) {
		this.message = createMessageFromParams(null, 'info', text)
		this.show = true
	}

	@Mutation
	setWarning(text: SetMessageParams) {
		this.message = createMessageFromParams(null, 'warning', text)
		this.show = true
	}
}

export type Message = {
	text: string
	type: 'success' | 'info' | 'warning' | 'error'
	timeout?: number
	errorDetails?: string
	stackTrace?: string
	/** Código curto do erro interno (ex.: ABC3-K7Q2-M9). É o que o cliente informa ao Suporte. */
	codigo?: string
	icon?: string
}

/**
 * Anota no histórico local o erro que acabou de ir para a tela.
 *
 * A rota sai de `window.location` em vez do vue-router de propósito: importar o router aqui criaria
 * um ciclo (o router importa guards, que importam stores), e o que interessa ao Suporte é o endereço
 * que o usuário estava vendo, que é exatamente o que está na barra do navegador.
 */
function registrarNoHistorico(message: Message): ErroNoHistorico[] {
	return registrarErroNoHistorico({
		codigo: message.codigo,
		// v-html no snackbar significa que o texto pode conter marcação; na lista ele é exibido como
		// texto puro, então as tags iriam aparecer cruas.
		texto: semTags(message.text),
		mensagem: semTags(message.errorDetails || ''),
		stackTrace: message.stackTrace,
		rota: typeof window !== 'undefined'
			? `${window.location.pathname}${window.location.search}`
			: '',
	})
}

export const semTags = (texto: string): string =>
	typeof texto === 'string' ? texto.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''

function createMessageFromParams(prefixo: string | null, type: Message['type'], content: string | AxiosError | {
	text: AxiosError | string
	timeout?: number
	icon?: string
}): Message {
	if (typeof content === 'string') {
		return {
			type,
			text: content,
		}
	}
	if (content instanceof Error) {
		const stackTrace = (content.response?.data as any)?.stack || ''
		const message = (content.response?.data as any)?.message || ''
		const codigo = codigoDoErro(content as AxiosError<any>)
		return {
			type,
			text: getRequestErrorMessage(prefixo, content),
			timeout: -1,
			// O código sai da mensagem porque ganhou campo próprio no diálogo de detalhes.
			errorDetails: semPrefixoDeCodigo(message),
			stackTrace: stackTrace,
			codigo: codigo,
		}
	}
	if (content.text instanceof Error) {
		return {
			...content,
			type,
			text: getRequestErrorMessage(prefixo, content.text),
		}
	}
	return {
		text: content.text as string,
		type,
		timeout: content.timeout,
		icon: content.icon,
	}
}

export type SetMessageParams = string | {
	text: string
	timeout?: number
	icon?: string
}

export type ErroComPrefixo = {
	error: SetMessageParams | unknown
	prefixo: string | null
}

export type SetErrorMessageParams = string | AxiosError | {
	text: string | AxiosError
	timeout?: number
}

export default getModule(AlertModule)